import React, { useState, useRef } from 'react';
import {
  priorityMeta, categoryMeta, fmtTime, fmtDuration, relativeDay, diffDays, SWIPE,
} from '../myDayUtils';
import useIsTouch from '../../../utils/useIsTouch';

/**
 * One line of the day, in two shapes.
 *
 * On a mouse the row keeps hover actions, which cost nothing when they are
 * hidden. On a touch device it becomes a swipe row instead — the pattern Daily
 * Operations already uses, so a swipe means the same thing everywhere in the
 * app. That was not a cosmetic choice: four 28px buttons pinned to every row
 * sat under the 44px touch minimum AND squeezed the title into two lines, so a
 * phone showed barely five activities a screen.
 *
 * Swiping is less discoverable than tapping, so the gesture is never the only
 * way through: the ⋯ button opens the same actions as a sheet, within reach of
 * a thumb.
 */

/** Meta chips, shared by both shapes. */
function Meta({ task, cats, today, showDate }) {
  const pri = priorityMeta(task.priority);
  const cat = task.category ? categoryMeta(task.category, cats) : null;
  const subDone = (task.subtasks || []).filter(s => s.done).length;
  const subTotal = (task.subtasks || []).length;
  const overdue = !task.done && diffDays(task.date, today) < 0;

  return (
    <span className="md-row__meta">
      <span className="md-flag" style={{ color: pri.color, borderColor: pri.color }}>{pri.short}</span>
      {/* Everything in the carried group has carried once; only a repeat
          offender earns a badge, and it sits early so the fade never eats it. */}
      {(task.carryCount || 0) > 1 && !task.done && (
        <span className="md-meta md-meta--carry"
          title={`Carried forward ${task.carryCount} times since ${task.originalDate}`}>
          ↪ ×{task.carryCount}
        </span>
      )}
      {task.startTime && <span className="md-meta md-meta--time">🕐 {fmtTime(task.startTime)}</span>}
      {task.estimateMin ? <span className="md-meta">⏱ {fmtDuration(task.estimateMin)}</span> : null}
      {cat && (
        <span className="md-meta md-meta--cat" style={{ color: cat.color, borderColor: `${cat.color}66` }}>
          {cat.icon} {cat.name}
        </span>
      )}
      {(task.tags || []).map(t => <span key={t} className="md-meta md-meta--tag">#{t}</span>)}
      {subTotal > 0 && (
        <span className={`md-meta${subDone === subTotal ? ' md-meta--ok' : ''}`}>☑ {subDone}/{subTotal}</span>
      )}
      {task.routineId && <span className="md-meta" title="Created by a routine">🔁</span>}
      {/* Everything in the carried group has carried at least once; only a
          repeat offender is worth a badge. */}
      {showDate && (
        <span className={`md-meta${overdue ? ' md-meta--late' : ''}`}>📅 {relativeDay(task.date, today)}</span>
      )}
      {task.focusSec > 0 && <span className="md-meta">🎯 {fmtDuration(Math.round(task.focusSec / 60))}</span>}
    </span>
  );
}

function Check({ task, onToggle, pri }) {
  return (
    <button
      className={`md-check${task.done ? ' md-check--on' : ''}`}
      style={task.done ? undefined : { borderColor: pri.color }}
      onClick={e => { e.stopPropagation(); onToggle(task); }}
      aria-pressed={task.done}
      aria-label={task.done ? `Mark "${task.title}" as not done` : `Mark "${task.title}" as done`}
    >
      <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="3.5">
        <polyline points="20 6 9 17 4 12" />
      </svg>
    </button>
  );
}

export default function TaskRow({
  task, cats, today, onToggle, onOpen, onAction, showDate = false,
  canMoveUp = false, canMoveDown = false,
}) {
  const isTouch = useIsTouch();
  const pri = priorityMeta(task.priority);
  const subDone = (task.subtasks || []).filter(s => s.done).length;
  const subTotal = (task.subtasks || []).length;

  // Priority is the row's own edge, so a P1 reads as urgent from across the
  // kitchen rather than from a small chip you have to stop and parse.
  const cls = [
    'md-row',
    task.done ? 'md-row--done' : '',
    !task.done && task.priority <= 2 ? `md-row--p${task.priority}` : '',
  ].filter(Boolean).join(' ');

  const [sheet, setSheet] = useState(false);
  const [dx, setDx] = useState(0);
  const [settling, setSettling] = useState(false);
  const start = useRef(null);
  const axis = useRef(null);
  const swiped = useRef(false);
  // The distance travelled is kept in a ref as well as in state: touchend can
  // fire in the same batch as the last touchmove, and reading the state there
  // would settle the gesture against a stale offset.
  const travel = useRef(0);

  const onTouchStart = e => {
    const t = e.touches[0];
    start.current = { x: t.clientX, y: t.clientY };
    axis.current = null;
    swiped.current = false;
  };
  const onTouchMove = e => {
    if (!start.current) return;
    const t = e.touches[0];
    const ddx = t.clientX - start.current.x;
    const ddy = t.clientY - start.current.y;
    if (axis.current === null) {
      if (Math.abs(ddx) < SWIPE.lock && Math.abs(ddy) < SWIPE.lock) return;
      axis.current = Math.abs(ddx) > Math.abs(ddy) ? 'h' : 'v';
    }
    if (axis.current !== 'h') return; // vertical — let the page scroll
    swiped.current = true;
    travel.current = Math.max(-SWIPE.max, Math.min(SWIPE.max, ddx));
    setDx(travel.current);
  };
  const onTouchEnd = () => {
    const travelled = travel.current;
    travel.current = 0;
    start.current = null;
    axis.current = null;
    if (travelled >= SWIPE.threshold) {
      setDx(0);
      onToggle(task);           // right — done
    } else if (travelled <= -SWIPE.threshold) {
      setSettling(true);
      setDx(-window.innerWidth); // left — delete, slide it out first
      setTimeout(() => onAction('delete', task), SWIPE.animMs);
    } else {
      setDx(0);
    }
  };

  /** A swipe must not also count as a tap on the row underneath it. */
  const openUnlessSwiped = () => {
    if (swiped.current) { swiped.current = false; return; }
    onOpen(task);
  };

  const act = (e, action) => { e.stopPropagation(); setSheet(false); onAction(action, task); };

  const body = (
    <>
      <Check task={task} onToggle={onToggle} pri={pri} />
      <button className="md-row__main" onClick={openUnlessSwiped} title="Open details">
        <span className="md-row__titleline">
          <span className="md-row__title">{task.title}</span>
          {task.pinned && <span className="md-row__pin" title="Pinned to the top">📌</span>}
        </span>
        <Meta task={task} cats={cats} today={today} showDate={showDate} />
        {subTotal > 0 && (
          <span className="md-row__subbar" aria-hidden="true">
            <span className="md-row__subbar-fill" style={{ width: `${(subDone / subTotal) * 100}%` }} />
          </span>
        )}
      </button>
    </>
  );

  if (isTouch) {
    return (
      <li className="md-swipe">
        {/* What each direction will do, revealed as the row moves off it. */}
        <span className="md-swipe__hint md-swipe__hint--done" aria-hidden="true"
          style={{ opacity: dx > 0 ? Math.min(1, dx / SWIPE.threshold) : 0 }}>✓ Done</span>
        <span className="md-swipe__hint md-swipe__hint--del" aria-hidden="true"
          style={{ opacity: dx < 0 ? Math.min(1, -dx / SWIPE.threshold) : 0 }}>Delete 🗑</span>
        <div
          className={cls}
          style={{ transform: `translateX(${dx}px)`, transition: start.current ? 'none' : `transform ${SWIPE.animMs}ms ease` }}
          onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}
        >
          {body}
          <button className="md-row__more" onClick={() => setSheet(true)}
            aria-label={`More actions for "${task.title}"`}>⋯</button>
        </div>

        {settling && <span className="md-sr" role="status">Deleting {task.title}</span>}

        {sheet && (
          <div className="md-sheet-back" onClick={() => setSheet(false)}>
            <div className="md-sheet" role="menu" onClick={e => e.stopPropagation()}>
              <span className="md-sheet__title">{task.title}</span>
              <button className="md-sheet__item" onClick={e => { e.stopPropagation(); setSheet(false); onOpen(task); }}>
                ✏️ Open details
              </button>
              {!task.done && <button className="md-sheet__item" onClick={e => act(e, 'focus')}>▶ Start a focus session</button>}
              {!task.done && <button className="md-sheet__item" onClick={e => act(e, 'tomorrow')}>→ Push to tomorrow</button>}
              {!task.done && <button className="md-sheet__item" onClick={e => act(e, 'nextweek')}>→ Push to next week</button>}
              <button className="md-sheet__item" onClick={e => act(e, 'pin')}>
                {task.pinned ? '📍 Unpin' : '📌 Pin to the top'}
              </button>
              {canMoveUp && <button className="md-sheet__item" onClick={e => act(e, 'up')}>↑ Move up</button>}
              {canMoveDown && <button className="md-sheet__item" onClick={e => act(e, 'down')}>↓ Move down</button>}
              <button className="md-sheet__item md-sheet__item--danger" onClick={e => act(e, 'delete')}>🗑 Delete</button>
              <button className="md-sheet__item md-sheet__cancel" onClick={() => setSheet(false)}>Cancel</button>
            </div>
          </div>
        )}
      </li>
    );
  }

  return (
    <li className={cls}>
      {body}
      <span className="md-row__actions">
        {canMoveUp && (
          <button className="md-iconbtn" onClick={e => act(e, 'up')} title="Move up" aria-label={`Move "${task.title}" up`}>↑</button>
        )}
        {canMoveDown && (
          <button className="md-iconbtn" onClick={e => act(e, 'down')} title="Move down" aria-label={`Move "${task.title}" down`}>↓</button>
        )}
        {!task.done && (
          <button className="md-iconbtn" onClick={e => act(e, 'focus')} title="Start a focus session" aria-label={`Start a focus session on "${task.title}"`}>▶</button>
        )}
        {!task.done && (
          <button className="md-iconbtn" onClick={e => act(e, 'tomorrow')} title="Push to tomorrow" aria-label={`Push "${task.title}" to tomorrow`}>→</button>
        )}
        <button className="md-iconbtn" onClick={e => act(e, 'pin')}
          title={task.pinned ? 'Unpin' : 'Pin to the top'}
          aria-label={task.pinned ? `Unpin "${task.title}"` : `Pin "${task.title}" to the top`}>
          {task.pinned ? '📌' : '📍'}
        </button>
        <button className="md-iconbtn md-iconbtn--danger" onClick={e => act(e, 'delete')}
          title="Delete" aria-label={`Delete "${task.title}"`}>✕</button>
      </span>
    </li>
  );
}
